/** The end of the game.
 *
 * A game that has been decided stops accepting commands: `evaluateVictory` sets
 * `victory.over`, `TurnLoop` stops with `stop = "victory"`, and the map simply
 * goes quiet. Before this screen existed that was the whole of the feedback -
 * the player sat in front of a board that had stopped answering and was never
 * told why. So this screen exists to say three things and then get out of the
 * way: who won, on what condition, and where everyone finished.
 *
 * It is a leaf like every other screen here. It reads no state, decides no
 * rule and knows nothing about the app model: the host resolves the condition's
 * subject (a town, a hero, an artifact all arrive as a name) and passes the two
 * routes out as callbacks.
 */

import { usePolishStyles } from "./polish.js";
import { ScreenStyles } from "./primitives.js";
import { groupDigits } from "./format.js";
import { playerColour } from "../theme/tokens.js";
import { StyleLayer, useStyleSheet } from "../theme/styleSheet.js";

export type GameOutcome = "victory" | "defeat" | "draw";

/** One player's final line. Counts are the observation's own, so a fog-limited
 *  view still gets a scoreboard (`ObservedOpponent` carries them for exactly
 *  this). */
export interface StandingRow {
  readonly name: string;
  /** `PlayerState.colour`; `playerColour` maps it to a swatch. */
  readonly colour: string;
  readonly towns: number;
  readonly heroes: number;
  readonly defeated: boolean;
  readonly winner: boolean;
  /** The viewer's own row, marked so a player can find himself. */
  readonly you: boolean;
}

export interface GameOverProps {
  readonly outcome: GameOutcome;
  /** Display name of the winner, or null when nobody took it. */
  readonly winnerName: string | null;
  /** The scenario condition, as `state.victory.condition` holds it. */
  readonly condition: string;
  /** Whatever the condition names, already resolved to something readable. */
  readonly conditionSubject?: string | null;
  readonly day: number;
  readonly week: number;
  readonly month: number;
  readonly standing: readonly StandingRow[];
  /** The viewer is out but the game runs on around him: with three players a
   *  human can lose his last town and his last hero while the survivors fight
   *  it out, and "nobody won" is not what happened to him. */
  readonly eliminated?: boolean;
  readonly onMainMenu: () => void;
  readonly onNewGame: () => void;
}

const HEADLINE: Readonly<Record<GameOutcome, string>> = Object.freeze({
  victory: "Victory",
  defeat: "Defeat",
  draw: "No One Prevailed",
});

/**
 * The scenario's win condition in words.
 *
 * `victory.condition` is `"<kind>"` or `"<kind>:<argument>"` so that a map file
 * can carry it as one string (`conditionWinner`, adventure/turn.ts). The
 * argument is an entity id or a number, which is no use to a player, so the
 * host resolves it to a name and this only has to pick the sentence.
 */
export function victoryConditionText(condition: string, subject?: string | null): string {
  const sep = condition.indexOf(":");
  const kind = sep < 0 ? condition : condition.slice(0, sep);
  const arg = sep < 0 ? "" : condition.slice(sep + 1);
  const named = subject !== null && subject !== undefined && subject !== "" ? subject : arg;
  switch (kind) {
    case "defeat_all": return "Defeat every rival";
    case "capture_town": return named === "" ? "Capture the target town" : `Capture ${named}`;
    case "defeat_hero": return named === "" ? "Defeat the target hero" : `Defeat ${named}`;
    case "acquire_artifact": return named === "" ? "Acquire the target artifact" : `Acquire the ${named}`;
    case "accumulate_gold": {
      const need = Number.parseInt(arg, 10);
      return Number.isFinite(need) ? `Accumulate ${groupDigits(need)} gold` : "Accumulate gold";
    }
    case "survive": {
      const days = Number.parseInt(arg, 10);
      return Number.isFinite(days)
        ? `Be the last standing, and no sooner than day ${String(days)}`
        : "Be the last standing";
    }
    case "survive_until_day": {
      const days = Number.parseInt(arg, 10);
      return Number.isFinite(days)
        ? `Hold out until day ${String(days)}`
        : "Hold out to the last day";
    }
    case "flag_all_mines": return "Own every mine on the map at once";
    case "build_grail": return "Raise the Grail structure in one of your towns";
    default: return "Defeat every rival";
  }
}

function plural(n: number, one: string, many?: string): string {
  return `${String(n)} ${n === 1 ? one : many ?? `${one}s`}`;
}

export function GameOver(props: GameOverProps): JSX.Element {
  usePolishStyles();
  useEndStyles();
  const { outcome } = props;

  return (
    <div className={`hs-screen hm-end hm-end--${outcome}`} role="region" aria-label="Game over">
      <ScreenStyles />

      <div className="hs-screen__bar hs-plank">
        <span className="hs-screen__title">The Game Is Over</span>
        <span className="hs-screen__sub">
          {`Month ${String(props.month)}, week ${String(props.week)}, day ${String(props.day)}`}
        </span>
        <span className="hs-spacer" />
      </div>

      <div
        className="hs-screen__body"
        style={{ justifyContent: "center", alignItems: "center", padding: "16px" }}
      >
        {/* Capped at the body's height so a scoreboard of eight players scrolls
            inside the card rather than running off the top of it. */}
        <div className="hs-result hs-scroll" style={{ width: "min(760px, 94vw)", maxHeight: "100%" }}>
          <div
            className="hs-result__banner"
            style={outcome === "victory" ? undefined : { color: "#e2c9a0" }}
          >
            {HEADLINE[outcome]}
          </div>
          <div className="hs-gilt-rule" style={{ width: "min(560px, 80vw)" }} />

          <div className="hs-tiny hs-muted" style={{ textAlign: "center" }}>
            {props.winnerName !== null
              ? outcome === "victory"
                ? "The realm is yours."
                : `${props.winnerName} has taken the realm.`
              : props.eliminated === true
                ? "Your last town and your last hero are gone. The war goes on without you."
                : "No one is left standing."}
          </div>

          <div className="hs-card hs-wood" style={{ width: "100%" }}>
            <div className="hs-card__title">Victory condition</div>
            <div className="hs-casualty">
              <span className="hs-casualty__name">
                {victoryConditionText(props.condition, props.conditionSubject)}
              </span>
              {/* A seal, not a debug flag. "met" / "unmet" in the numeral face
                  at the right margin read as a field dump on the one screen a
                  player is meant to remember. */}
              <span className={`hm-end__seal ${props.winnerName === null ? "hm-end__seal--no" : ""}`}>
                {props.winnerName === null ? "Unfulfilled" : "Fulfilled"}
              </span>
            </div>
          </div>

          <div className="hs-card hs-wood" style={{ width: "100%" }}>
            <div className="hs-card__title">Final standing</div>
            {props.standing.length === 0 ? (
              <div className="hs-tiny hs-muted">No players.</div>
            ) : props.standing.map((row) => (
              <div className="hs-casualty" key={row.name}>
                <span
                  aria-hidden="true"
                  style={{
                    width: 12, height: 12, flex: "0 0 auto",
                    background: playerColour(row.colour),
                    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.8)",
                  }}
                />
                <span className="hs-casualty__name">
                  {row.name}
                  {row.you ? " (you)" : ""}
                  {row.winner ? " — victor" : row.defeated ? " — defeated" : ""}
                </span>
                <span className="hs-casualty__num">{plural(row.towns, "town")}</span>
                <span className="hs-casualty__num">{plural(row.heroes, "hero", "heroes")}</span>
              </div>
            ))}
          </div>

          <div className="hs-row" style={{ gap: 12, justifyContent: "center", paddingTop: 4 }}>
            <button type="button" className="hs-plate" onClick={props.onNewGame}>New Game</button>
            <button type="button" className="hs-plate" onClick={props.onMainMenu}>Main Menu</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ styles

const END_STYLE_ID = "hm-game-over";

/* The shared hs-screen rule paints a cold blue-grey wash: the right ground for
   a settings panel and the wrong one for the end of a campaign, where on the
   shipped shot it filled everything the two small cards did not. The board is
   lit from above and tinted by the outcome - warm for a win, ashen for a loss -
   so the screen says which it is before a word is read. */
const END_CSS = `
.hs-screen.hm-end {
  background-color: #24170c;
  background-image:
    radial-gradient(90% 70% at 50% 6%, rgba(255,214,150,0.12) 0%, rgba(0,0,0,0) 60%),
    radial-gradient(120% 95% at 50% 0%, rgba(0,0,0,0) 20%, rgba(0,0,0,0.85) 100%),
    repeating-linear-gradient(122deg, rgba(255,220,170,0.03) 0 2px, rgba(0,0,0,0.08) 2px 6px),
    linear-gradient(180deg, #3a2312 0%, #221408 58%, #120a04 100%);
}
.hs-screen.hm-end--defeat {
  background-image:
    radial-gradient(90% 70% at 50% 6%, rgba(190,200,215,0.10) 0%, rgba(0,0,0,0) 60%),
    radial-gradient(120% 95% at 50% 0%, rgba(0,0,0,0) 20%, rgba(0,0,0,0.88) 100%),
    repeating-linear-gradient(122deg, rgba(210,220,235,0.03) 0 2px, rgba(0,0,0,0.10) 2px 6px),
    linear-gradient(180deg, #2b2b33 0%, #191920 58%, #0c0c10 100%);
}
.hm-end .hs-result__banner { font-size: clamp(34px, 4.2vw, 72px); }
.hm-end__seal {
  flex: 0 0 auto;
  padding: 4px 16px 5px;
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(12px, var(--hm-type-floor));
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #2c2008;
  background-image:
    radial-gradient(74% 60% at 30% 12%, rgba(255,255,240,0.7) 0%, rgba(255,255,240,0) 60%),
    linear-gradient(172deg, #ffeab4 0%, #d9b25a 48%, #97711a 100%);
  box-shadow:
    inset 0 0 0 1px rgba(48,34,8,0.9),
    inset 0 1px 0 rgba(255,255,240,0.8),
    0 2px 5px rgba(0,0,0,0.5);
  clip-path: polygon(7px 0%, 100% 0%, 100% calc(100% - 7px), calc(100% - 7px) 100%, 0% 100%, 0% 7px);
  text-shadow: 0 1px 0 rgba(255,250,225,0.5);
}
.hm-end__seal--no {
  color: #e8ddc6;
  background-image: linear-gradient(172deg, #6a5c42 0%, #4c4130 52%, #2c2419 100%);
  box-shadow: inset 0 0 0 1px rgba(38,30,10,0.8), inset 0 1px 0 rgba(255,244,212,0.18);
  text-shadow: 0 1px 0 rgba(0,0,0,0.5);
}
`;

function useEndStyles(): void {
  useStyleSheet(END_STYLE_ID, END_CSS, StyleLayer.screen);
}
